import React, { useState, useEffect } from 'react';
import { TrendingUp, BarChart3, MessageSquare, Activity, Target, Users, Shield, PhoneOutgoing, PhoneIncoming, ReceiptText, CalendarX, PhoneOff, PhoneCall, Clock, MessageCircleMore } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_URL } from '../config/api';
import { useClient } from '../contexts/ClientContext';
import { useAnalytics, useGoContactStats } from '../hooks/useApi';
import TabSystem from './TabSystem';
import { getTabsConfig } from '../config/lookerDashboards';
import FloatingParticles from './FloatingParticles';
import PageHeader from './ui/PageHeader';
import '../styles/neutral-theme.css';
import '../styles/analytics-metrics.css';
import '../styles/analytics-headers-override.css';
import '../styles/analytics-cards-override.css';
import '../styles/analytics-evolution-modern.css';
import '../styles/analytics-campaign-results.css';
import EvolutionGraphic from './GraficoEvolucionLlamadas';

const DASHBOARD_CONFIG_FALLBACK = {
  sections: {
    analytics: {
      enabled: true,
      dataSource: 'main',
      fields: {
        // Visibles
        total_calls: true,
        attended_calls: true,
        abandoned_calls: true,
        avg_duration: true,
        call_distribution: true,
        contrataciones: true,
        occupancy: true,
        tma: true,
        tme: true,
        evolution_chart: true,
        campaign_results: true,
        looker_tabs: true,
        // Datos fuente
        call_start: true,
        call_type: true,
        abandonadas: true,
        talk_time: true,
        hold_time: true,
        wrap_up_time: true,
        contact_id: true,
        campaign: true,
        hiring: true,
      },
      fieldMappings: {
        // Visibles (sin mapeo)
        total_calls: null,
        attended_calls: null,
        abandoned_calls: null,
        avg_duration: null,
        call_distribution: null,
        contrataciones: null,
        occupancy: null,
        tma: null,
        tme: null,
        evolution_chart: null,
        campaign_results: null,
        looker_tabs: null,
        // Datos fuente (GoContact sugerido)
        call_start: { table: 'datos_go_contact', column: 'call_start' },
        call_type: { table: 'datos_go_contact', column: 'call_type' },
        abandonadas: { table: 'datos_go_contact', column: 'abandonadas' },
        talk_time: { table: 'datos_go_contact', column: 'talk_time' },
        hold_time: { table: 'datos_go_contact', column: 'hold_time' },
        wrap_up_time: { table: 'datos_go_contact', column: 'wrap_up_time' },
        contact_id: { table: 'datos_go_contact', column: 'contact_id' },
        campaign: { table: 'datos_go_contact', column: 'campana' },
        hiring: { table: 'datos_go_contact', column: 'segmento' },
      },
      fieldCalculations: {},
    },
  },
};

const AnalyticsPanel = () => {
  const { t } = useLanguage();
  const { clientConfig, isAdmin, getClientDisplayName } = useClient();
  const [analyticsType, setAnalyticsType] = useState('outbound'); // outbound or inbound
  const [dashboardConfig, setDashboardConfig] = useState(DASHBOARD_CONFIG_FALLBACK);
  const [configLoading, setConfigLoading] = useState(false);

  // Usar datos reales de analytics con filtrado por cliente
  const { data: analyticsData, loading: analyticsLoading, error: analyticsError, refetch: refetchAnalytics } = useAnalytics(analyticsType, { enablePolling: false });
  const { occupancy, answered, abandoned, tma, tme, loading: gcLoading, error: gcError, refetch: refetchGoContact } = useGoContactStats(analyticsType);
  const [evolutionMetrics, setEvolutionMetrics] = useState({
    periodo: '-',
    totalLlamadas: 0,
    tasaRespuesta: 0,
    promedioRealizadas: 0,
    promedioContestadas: 0,
    horasActivas: 0,
  });
  const contrataciones = analyticsData?.contrataciones; // Puede ser null

  useEffect(() => {
    const fetchConfig = async () => {
      setConfigLoading(true);
      try {
        const resp = await fetch(`${API_URL}/api/admin/dashboard-config`, { credentials: 'include' });
        if (resp.ok) {
          const json = await resp.json();
          setDashboardConfig(json.config || DASHBOARD_CONFIG_FALLBACK);
        } else {
          setDashboardConfig(DASHBOARD_CONFIG_FALLBACK);
        }
      } catch (e) {
        setDashboardConfig(DASHBOARD_CONFIG_FALLBACK);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Si vuelves desde Admin (o cambias de pestaña), refrescar KPIs para reflejar cambios de cálculo/config.
  useEffect(() => {
    const onFocus = () => {
      try { refetchAnalytics?.(); } catch (e) { /* noop */ }
      try { refetchGoContact?.(); } catch (e) { /* noop */ }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetchAnalytics, refetchGoContact]);

  const sectionCfg = (dashboardConfig?.sections?.analytics) || DASHBOARD_CONFIG_FALLBACK.sections.analytics;
  const fieldsCfg = sectionCfg?.fields || {};
  const mappingsCfg = sectionCfg?.fieldMappings || {};
  const fieldOn = (key) => (fieldsCfg[key] ?? true);
  const isSectionEnabled = sectionCfg?.enabled !== false;
  const getFieldLabel = (fieldKey, fallbackLabel) => {
    const raw = sectionCfg?.fieldLabels?.[fieldKey];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return fallbackLabel;
  };

  const mappingMissing = (key) => {
    const m = mappingsCfg[key];
    return !m || !m.table || !m.column;
  };

  // Solo avisar por mapeos de datos base (no por métricas calculadas)
  const requiredMappingKeys = [
    'call_start',
    'call_type',
    'abandonadas',
    'talk_time',
    'hold_time',
    'wrap_up_time',
    'contact_id',
    'campaign',
    'hiring',
  ];

  const enabledRequiredMappingKeys = requiredMappingKeys.filter((k) => {
    // Evitar ruido: algunos campos solo son necesarios si el bloque está activo
    if (['campaign', 'hiring', 'contact_id'].includes(k)) {
      return fieldOn('campaign_results') || fieldOn('contrataciones');
    }
    if (['hold_time', 'wrap_up_time'].includes(k)) {
      return fieldOn('avg_duration') || fieldOn('campaign_results');
    }
    if (k === 'talk_time') {
      return fieldOn('attended_calls') || fieldOn('avg_duration') || fieldOn('campaign_results');
    }
    if (k === 'abandonadas') {
      return fieldOn('attended_calls') || fieldOn('abandoned_calls') || fieldOn('avg_duration') || fieldOn('campaign_results');
    }
    return true;
  });

  const missingMappings = enabledRequiredMappingKeys.filter((k) => mappingMissing(k));

  const METRIC_NAME_TO_CFG_KEY = {
    totalCalls: 'total_calls',
    attendedCalls: 'attended_calls',
    abandonedCalls: 'abandoned_calls',
    avgDuration: 'avg_duration',
  };

  // Métricas basadas en datos reales (filtradas por config)
  const metrics = (analyticsData && analyticsData.metrics ? analyticsData.metrics : [])
    .filter((metric) => {
      const cfgKey = METRIC_NAME_TO_CFG_KEY[metric.name];
      return !cfgKey || fieldOn(cfgKey);
    })
    .map(metric => ({
    label: t(`analytics.${metric.name}`),
    value: metric.value,
      name: metric.name,
  }));

  const showCallDistribution = fieldOn('call_distribution');
  const showContrataciones = fieldOn('contrataciones');
  const showOccupancy = fieldOn('occupancy');
  const showAttended = fieldOn('attended_calls');
  const showAbandoned = fieldOn('abandoned_calls');
  const showTma = fieldOn('tma');
  const showTme = fieldOn('tme');
  const showEvolutionChart = fieldOn('evolution_chart');
  const showCampaignResults = fieldOn('campaign_results');
  const showLookerTabs = fieldOn('looker_tabs');


  /* ============================================
    Panel de Métricas Generales
    ============================================ */
  const callResults = [
    { type: 'Contrataciones', count: '71 mil', percentage: 100, color: '#10b981', icon: 'contrataciones' },
    { type: 'Ocupación', count: '71 mil', percentage: 48, color: '#06b6d4', icon: 'ocupacion' },
    { type: 'Atendidas', count: '71 mil', percentage: 48, color: '#3b82f6', icon: 'atendidas' },
    { type: 'Abandonadas', count: '71 mil', percentage: 48, color: '#ef4444', icon: 'abandonadas' },
    { type: 'TMA', count: '45 mil', percentage: 100, color: '#8b5cf6', icon: 'tma' },
    { type: 'TME', count: '126 mil', percentage: 100, color: '#f59e0b', icon: 'tme' }
  ];

  /* ============================================
  Panel de Distribución de Resultados de Llamadas
  ============================================ */

  const formatSecondsToMmSs = (seconds) => {
    const total = Math.round(Number(seconds || 0));
    const minutes = Math.floor(total / 60);
    const secs = total % 60;
    return `${minutes}m ${secs}s`;
  };
  const appendPercentIfNumeric = (value) => {
    if (value == null) return value;
    const str = String(value);
    if (!/[0-9]/.test(str)) return str;
    return str.includes('%') ? str : `${str}%`;
  };

  const callResultsValues = callResults.map(item => {
    if (item.icon === 'contrataciones') {
      const display = contrataciones != null ? contrataciones : (gcLoading ? '...' : '--');
      return { ...item, type: getFieldLabel('contrataciones', t('analytics.contracts')), count: display };
    }
    if (item.icon === 'ocupacion') {
      const display = occupancy != null ? occupancy : (gcLoading ? '...' : '--');
      return { ...item, icon: 'ocupacion', type: getFieldLabel('occupancy', t('analytics.occupancy')), count: appendPercentIfNumeric(display), percentage: display };
    }
    if (item.icon === 'atendidas') {
      const display = answered != null ? answered : (gcLoading ? '...' : '--');
      return { ...item, icon: 'atendidas', type: getFieldLabel('attended_calls', t('analytics.attendedCalls')), count: appendPercentIfNumeric(display), percentage: display };
    }
    if (item.icon === 'abandonadas') {
      const display = abandoned != null ? abandoned : (gcLoading ? '...' : '--');
      return { ...item, icon: 'abandonadas', type: getFieldLabel('abandoned_calls', t('analytics.abandonedCalls')), count: appendPercentIfNumeric(display), percentage: display };
    }
    if (item.icon === 'tma') {
      const display = tma != null ? formatSecondsToMmSs(tma) : (gcLoading ? '...' : '--');
      return { ...item, type: getFieldLabel('tma', t('analytics.tma')), count: display };
    }
    if (item.icon === 'tme') {
      const display = tme != null ? formatSecondsToMmSs(tme) : (gcLoading ? '...' : '--');
      return { ...item, type: getFieldLabel('tme', t('analytics.tme')), count: display };
    }
    return item;
  });

  const callResultsValuesFiltered = callResultsValues.filter((result) => {
    if (result.icon === 'contrataciones') return showContrataciones;
    if (result.icon === 'ocupacion') return showOccupancy;
    if (result.icon === 'atendidas') return showAttended;
    if (result.icon === 'abandonadas') return showAbandoned;
    if (result.icon === 'tma') return showTma;
    if (result.icon === 'tme') return showTme;
    return true;
  });

  const showCallDistributionBlock = Boolean(showCallDistribution && callResultsValuesFiltered.length > 0);

    /* ============================================
  Panel de Evolución de Llamadas
  ============================================ */

  // Este está abajo

    /* ============================================
  Panel de Resultados de Campañas
  ============================================ */

  // Formatear duración media de segundos a mm:ss
  const formatDurationToMmSs = (seconds) => {
    const totalSeconds = Math.round(Number(seconds || 0));
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Usar datos reales de campañas del backend
  const campaignResults = (analyticsData?.campaigns || []).map(campaign => ({
    ...campaign,
    avgDuration: formatDurationToMmSs(campaign.avgDuration)
  }));

  // Configure Looker dashboard tabs using the configuration file
  const { locale } = useLanguage();
  
  const getLookerTabs = () => {
    const tabsConfig = getTabsConfig('analytics', locale, clientConfig);

    // Icon mapping
    const iconMapping = {
      'BarChart3': BarChart3,
      'MessageSquare': MessageSquare,
      'Activity': Activity,
      'Target': Target,
      'Users': Users,
      'Shield': Shield
    };

    return tabsConfig.map(tab => ({
        ...tab,
      icon: iconMapping[tab.icon] || BarChart3
    }));
  };

  const handleLookerTabChange = (tabId) => {
    // Tab changed
  };

  const getResultBarWidth = (result) => {
    const isCountDriven = result && ['ocupacion', 'atendidas', 'abandonadas'].includes(result.icon);

    const parsePercent = (value) => {
      if (value == null) return NaN;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const match = value.replace(',', '.').match(/[-+]?[0-9]*\.?[0-9]+/);
        return match ? parseFloat(match[0]) : NaN;
      }
      return NaN;
    };

    const raw = isCountDriven ? result?.count : result?.percentage;
    let width = parsePercent(raw);
    if (!Number.isFinite(width)) width = 100;
    if (width < 0) width = 0;
    if (width > 100) width = 100;
    return width;
  };

  // Sincronizar refresco: cuando se refresque analytics, refrescar también GoContact
  // y viceversa en intervalos coordinados
  React.useEffect(() => {
    // Un solo polling coordinado para ambos datasets
    const doRefetch = () => {
      if (typeof refetchAnalytics === 'function') refetchAnalytics();
      if (typeof refetchGoContact === 'function') refetchGoContact();
    };
    doRefetch();
    const interval = setInterval(doRefetch, 30000);
    return () => clearInterval(interval);
  }, [analyticsType, refetchAnalytics, refetchGoContact]);

  return (
    <div className="analytics-panel dashboard-container">
      <FloatingParticles count={25} />
      
      <PageHeader
        title={isAdmin ? 'Analytics Globales' : 
          analyticsType === 'outbound' ? 'Analíticas Outbound' : 'Analíticas Inbound'
        }
        subtitle={isAdmin ? 'Vista global de todos los clientes' : `Toda la información a un click para ${getClientDisplayName()}`}
        icon={<BarChart3 />}
        badge={!isAdmin ? getClientDisplayName() : null}
        actions={
          <>
            <button
              className={`analytics-type-btn ${analyticsType === 'outbound' ? 'active' : ''}`}
              onClick={() => setAnalyticsType('outbound')}
            >
              <PhoneOutgoing size={16} />
              <span>{t('analyticsTypes.outbound')}</span>
            </button>
            <button
              className={`analytics-type-btn ${analyticsType === 'inbound' ? 'active' : ''}`}
              onClick={() => setAnalyticsType('inbound')}
            >
              <PhoneIncoming size={16} />
              <span>{t('analyticsTypes.inbound')}</span>
            </button>
          </>
        }
      />

      {!configLoading && !isSectionEnabled && (
        <div className="message-enhanced info" style={{ marginTop: '12px' }}>
          La sección de Analíticas está desactivada en la configuración.
        </div>
      )}
      {!configLoading && isSectionEnabled && missingMappings.length > 0 && (
        <div className="message-enhanced warning" style={{ marginTop: '12px' }}>
          Faltan mapeos para: {missingMappings.join(', ')}. Completa tabla/columna en Configurar vistas.
        </div>
      )}

      {/* Loading State */}
      {analyticsLoading && (
        <div className="loading-enhanced">
          <div className="spinner-enhanced"></div>
          <p>Cargando datos de analytics...</p>
        </div>
      )}

      {/* Error State */}
      {analyticsError && (
        <div className="message-enhanced error">
          <p>Error al cargar los datos: {analyticsError}</p>
          <button className="btn-enhanced" onClick={() => window.location.reload()}>Reintentar</button>
        </div>
      )}

      {/* ============================================
         Panel de Métricas Generales
         ============================================ */}

      {/* Main Metrics Ultra Modern */}
      {!analyticsLoading && !analyticsError && isSectionEnabled && metrics.length > 0 && (
        <div className="metrics-grid">
          {metrics.map((metric, index) => (
            <div key={index} className="metric-card-ultra-modern">
              <div className="metric-header-ultra">
                <div className="metric-label-container-ultra">
                  <span className="metric-label-ultra">{metric.label}</span>       
                </div>
                <div className="metric-icon-ultra">
                  <TrendingUp size={16} />
                </div>
              </div>
              
              <div className="metric-value-container-ultra">
                <div className="metric-value-ultra">{metric.value}</div>
                <div className="metric-value-glow"></div>
              </div>
              
              <div className="metric-background-ultra"></div>     
            </div>
          ))}
        </div>
      )}

      {isSectionEnabled && !analyticsError && (showCallDistributionBlock || showEvolutionChart) && (
      <div className="charts-grid">
        {/* ============================================
         Panel de Distribución de Resultados de Llamadas
         ============================================ */}
        {/* Call Distribution Ultra Modern */}
        {showCallDistributionBlock && (
        <div className="card card-enhanced animate-delay-200 call-distribution-ultra">
          <div className="card-header card-header-ultra">
            <div className="header-content">
              <div className="header-icon">
                <BarChart3 size={24} />
              </div>
              <div className="header-text">
            <h3 className="card-title">{getFieldLabel('call_distribution', t('analytics.callDistribution'))}</h3>
                <p className="card-subtitle">Análisis detallado de resultados por categoría</p>
              </div>
            </div>
            <div className="header-glow"></div>
          </div>
          <div className="card-content">
            <div className="call-results-ultra">
              {callResultsValuesFiltered.map((result, index) => (
                <div key={index} className="result-item-ultra">
                  <div className="result-header-ultra">
                    <div className="result-icon-container">
                    <div className="result-icon" style={{ backgroundColor: result.color }}>
                      {result.icon === 'contrataciones' && <ReceiptText size={20} />}
                      {result.icon === 'ocupacion' && <CalendarX size={20} />}
                      {result.icon === 'atendidas' && <PhoneCall size={20} />}
                      {result.icon === 'abandonadas' && <PhoneOff size={20} />}
                      {result.icon === 'tma' && <Clock size={20} />}
                      {result.icon === 'tme' && <MessageCircleMore size={20} />}
                      </div>
                    </div>
                    <div className="result-info-ultra">
                      <div className="result-label-ultra">{result.type}</div>
                      <div className="result-value-ultra">{result.count}</div>
                    </div>
                    {/* Indicadores de porcentaje/cambio eliminados */}
                  </div>
                  
                  <div className="result-bar-container-ultra">
                    <div className="result-bar-ultra">
                      <div 
                        className="result-progress-ultra" 
                        style={{ 
                          width: `${getResultBarWidth(result)}%`, 
                          background: `linear-gradient(90deg, ${result.color} 0%, ${result.color}80 100%)`
                        }}
                      >
                        <div className="progress-glow"></div>
                        <div className="progress-shimmer"></div>
                  </div>
                      <div className="bar-background-ultra"></div>
                    </div>
                    <div className="bar-indicators">
                      <div className="indicator-dot"></div>
                      <div className="indicator-line"></div>
                    </div>
                  </div>
                  
                  <div className="result-stats-ultra">
                    <div className="stat-item">
                      <span className="stat-label">Volumen</span>
                      <span className="stat-value">{result.count}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Tendencia</span>
                      <span className="stat-value positive">+{result.percentage}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* ============================================
         Panel de Evolución de Llamadas
         ============================================ */}
        {/* Evolution Chart Ultra Modern */}
        {showEvolutionChart && (
        <div className="card card-enhanced animate-delay-300 call-evolution-ultra">
          <div className="card-header card-header-ultra">
            <div className="header-content">
              <div className="header-icon">
                <Activity size={24} />
              </div>
              <div className="header-text">
            <h3 className="card-title">{t('analytics.callEvolution')}</h3>
                <p className="card-subtitle">Tendencia temporal de llamadas por período</p>
              </div>
            </div>
            <div className="header-glow"></div>
          </div>
          <div className="card-content">
            <div className="evolution-container-ultra">
              <div className="evolution-header-ultra">
                <div className="evolution-info">
                  <div className="info-item">
                    <span className="info-label">Período</span>
                    <span className="info-value">{evolutionMetrics.periodo}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Total Llamadas</span>
                    <span className="info-value">{Number(evolutionMetrics.totalLlamadas || 0).toLocaleString()}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tasa de Respuesta</span>
                    <span className="info-value">{`${evolutionMetrics.tasaRespuesta}%`}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Promedio por Hora</span>
                    <span className="info-value">{`${evolutionMetrics.promedioRealizadas} / ${evolutionMetrics.promedioContestadas}`}</span>
                  </div>
                </div>
              </div>
              <EvolutionGraphic 
                hourlyCalls={analyticsData?.calls_by_hours || {}} 
                hourlyAnswered={analyticsData?.calls_by_hours_answered || {}} 
                onMetricsChange={setEvolutionMetrics}
              />                         
            </div>
          </div>
        </div>
        )}
      </div>
      )}

      {/* ============================================
         Panel de Resultados de Campañas
         ============================================ */}
      {/* Campaign Results Table */}
      {isSectionEnabled && !analyticsError && showCampaignResults && (
      <div className="card card-enhanced animate-delay-400 campaign-results-ultra">
        <div className="card-header card-header-ultra">
          <div className="header-content">
            <div className="header-icon">
              <BarChart3 size={24} />
            </div>
            <div className="header-text">
          <h3 className="card-title">{t('analytics.campaignResults')}</h3>
              <p className="card-subtitle">Análisis detallado de rendimiento por campaña</p>
            </div>
          </div>
          <div className="header-glow"></div>
        </div>
        <div className="card-content">
          <div className="campaign-results-container">
            <div className="results-table-modern">
              <table className="results-table">
                <thead className="table-header-modern">
                  <tr className="header-row">
                    <th className="header-cell campaign-col">{t('campaigns.title')}</th>
                    <th className="header-cell metric-col">{t('analytics.totalCalls')}</th>
                    <th className="header-cell metric-col">{t('campaigns.contacts')}</th>
                    <th className="header-cell metric-col">{t('analytics.contracts')}</th>
                    <th className="header-cell metric-col">{t('analytics.attendedCalls')}</th>
                    <th className="header-cell metric-col">{t('analytics.abandonedCalls')}</th>
                    <th className="header-cell metric-col">{t('analytics.avgDuration')}</th>
                  </tr>
                </thead>
                <tbody className="table-body-modern">
                  {campaignResults.map((campaign, index) => {
                    // Calcular porcentajes basados en totalCalls, evitar división por cero
                    const totalCalls = campaign.totalCalls || 0;
                    const contactsPercent = totalCalls > 0 ? ((campaign.contacts / totalCalls) * 100).toFixed(1) : '0.0';
                    const contractsPercent = totalCalls > 0 ? ((campaign.contracts / totalCalls) * 100).toFixed(1) : '0.0';
                    const answeredPercent = totalCalls > 0 ? ((campaign.answered / totalCalls) * 100).toFixed(1) : '0.0';
                    const abandonedPercent = totalCalls > 0 ? ((campaign.abandoned / totalCalls) * 100).toFixed(1) : '0.0';

                    return (
                      <tr
                        key={index}
                        className="campaign-row-modern"
                        onClick={() => console.log(`${t('table.detailOf')}: ${campaign.name}`)}
                      >
                        <td className="row-cell campaign-cell">
                          <div className="campaign-info">
                            <div className="campaign-name">{campaign.name}</div>
                            <div className="campaign-indicator"></div>
                          </div>
                        </td>
                        <td className="row-cell metric-cell">
                          <div className="metric-container">
                            <span className="metric-value">{totalCalls.toLocaleString()}</span>
                          </div>
                        </td>
                        <td className="row-cell metric-cell">
                          <div className="metric-container">
                            <span className="metric-value">{campaign.contacts.toLocaleString()}</span>
                            <span className="metric-percentage success">{contactsPercent}%</span>
                          </div>
                        </td>
                        <td className="row-cell metric-cell">
                          <div className="metric-container">
                            <span className="metric-value">{campaign.contracts.toLocaleString()}</span>
                            <span className="metric-percentage warning">{contractsPercent}%</span>
                          </div>
                        </td>
                        <td className="row-cell metric-cell">
                          <div className="metric-container">
                            <span className="metric-value">{campaign.answered.toLocaleString()}</span>
                            <span className="metric-percentage info">{answeredPercent}%</span>
                          </div>
                        </td>
                        <td className="row-cell metric-cell">
                          <div className="metric-container">
                            <span className="metric-value">{campaign.abandoned.toLocaleString()}</span>
                            <span className="metric-percentage danger">{abandonedPercent}%</span>
                          </div>
                        </td>
                        <td className="row-cell metric-cell">
                          <div className="metric-container">
                            <span className="metric-value">{campaign.avgDuration}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card">
                <div className="summary-icon">
                  <Target size={20} />
                </div>
                <div className="summary-content">
                  <div className="summary-value">
                    {campaignResults.reduce((sum, campaign) => sum + (campaign.contacts || 0), 0).toLocaleString()}
                  </div>
                  <div className="summary-label">Total Contactos</div>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="summary-icon">
                  <BarChart3 size={20} />
                </div>
                <div className="summary-content">
                  <div className="summary-value">
                    {(() => {
                      const totalCalls = campaignResults.reduce((sum, campaign) => sum + (campaign.totalCalls || 0), 0);
                      const totalAnswered = campaignResults.reduce((sum, campaign) => sum + (campaign.answered || 0), 0);
                      const tasaRespuesta = totalCalls > 0 ? ((totalAnswered / totalCalls) * 100).toFixed(1) : '0.0';
                      return `${tasaRespuesta}%`;
                    })()}
                  </div>
                  <div className="summary-label">Tasa de Respuesta</div>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="summary-icon">
                  <TrendingUp size={20} />
                </div>
                <div className="summary-content">
                  <div className="summary-value">
                    {(() => {
                      const totalAnswered = campaignResults.reduce((sum, campaign) => sum + (campaign.answered || 0), 0);
                      const totalContracts = campaignResults.reduce((sum, campaign) => sum + (campaign.contracts || 0), 0);
                      const porcentajeContrataciones = totalAnswered > 0 ? ((totalContracts / totalAnswered) * 100).toFixed(1) : '0.0';
                      return `${porcentajeContrataciones}%`;
                    })()}
                  </div>
                  <div className="summary-label">% Contrataciones</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ============================================
         Panel de Informe Detallado - Análisis Avanzado
         ============================================ */}
      {/* Looker Dashboard Tabs */}
      {isSectionEnabled && showLookerTabs && (
      <div className="card">
        <div className="card-header card-header-ultra">
          <div className="header-content">
            <div className="header-icon">
              <BarChart3 size={24} />
            </div>
            <div className="header-text">
          <h3 className="card-title">{t('analytics.dashboardReport')}</h3>
          <p className="card-subtitle">{t('analytics.dashboardPlaceholder')}</p>
            </div>
          </div>
          <div className="header-glow"></div>
        </div>
        <div className="card-content">
          <TabSystem
            tabs={getLookerTabs()}
            defaultTab="general-metrics"
            style="modern"
            onTabChange={handleLookerTabChange}
            showIframeControls={true}
          />
        </div>
      </div>      
      )}
    </div>
  );
};

export default AnalyticsPanel;